import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
    Key, Building, Users, Stethoscope, MapPin, User,
    Folder, Calendar, ClipboardList, Pill, FlaskConical,
    BarChart, MessageCircle, UserCircle, LogOut 
} from 'lucide-react';

// --- Constantes y Mapeo de Iconos ---
// Mapeo de las cadenas de texto (icon: 'key', 'building', etc.) a los componentes de Lucide React
const IconMap = {
    'key': Key,                 // Roles
    'building': Building,       // Clínicas
    'users': Users,             // Usuarios
    'stethoscope': Stethoscope, // Médicos
    'hospital': MapPin,         // Consultorios (Usamos MapPin como un ícono de ubicación/lugar)
    'user': User,               // Pacientes (Usuario simple)
    'folder': Folder,           // Expedientes
    'calendar': Calendar,       // Citas
    'clipboard': ClipboardList, // Recetas
    'capsule': Pill,            // Medicamentos (Usamos Pill para cápsulas/medicinas)
    'flask': FlaskConical,      // Laboratorios (Matraz cónico)
    'bar-chart': BarChart,      // Reportes
    'message-circle': MessageCircle, // Chat
    'user-circle': UserCircle,  // Perfil
    'logout': LogOut,           // Cerrar Sesión
};

const navLinks = [
    // 1. Elementos de Administración
    { to: '/roles', icon: 'key', label: 'Roles' }, 
    { to: '/clinicas', icon: 'building', label: 'Clinicas' },
    { to: '/usuarios', icon: 'users', label: 'Usuarios' },
    
    // 2. Elementos Operativos
    { to: '/medicos', icon: 'stethoscope', label: 'Médicos' },
    { to: '/consultorios', icon: 'hospital', label: 'Consultorios' },
    { to: '/pacientes', icon: 'user', label: 'Pacientes' },
    
    { to: '/expedientes', icon: 'folder', label: 'Expedientes' },
    
    { to: '/citas', icon: 'calendar', label: 'Citas' },
    { to: '/recetas', icon: 'clipboard', label: 'Recetas' },
    
    { to: '/medicamentos', icon: 'capsule', label: 'Medicamentos' }, 
    
    { to: '/laboratorios', icon: 'flask', label: 'Laboratorios' },
    { to: '/reportes', icon: 'bar-chart', label: 'Reportes' },
    
    // 3. Comunicación y Perfil
    { to: '/chat', icon: 'message-circle', label: 'Chat' },
    { to: '/perfil', icon: 'user-circle', label: 'Perfil' },
];

/**
 * Componente que renderiza el icono de Lucide basado en el nombre.
 */
const IconComponent = ({ name, className = "" }) => {
    const Icon = IconMap[name];
    // Si no se encuentra el icono, retorna un span vacío para evitar errores
    if (!Icon) return <span className={`mr-3 w-5 h-5 ${className}`}>?</span>;
    
    // Renderiza el componente de Lucide
    return <Icon className={`mr-3 w-5 h-5 ${className}`} />;
};

/**
 * Componente de la barra lateral de navegación (Sidebar).
 * Ahora usa IconComponent para iconos modernos.
 */
export default function Sidebar() {
    const navigate = useNavigate();

    const handleLogout = () => {
        // En una aplicación real, usarías un servicio de autenticación (como Firebase Auth)
        localStorage.removeItem('authToken');
        console.log('Sesión cerrada. Token eliminado.');
        // Redirige al login o a la página principal
        navigate('/login'); 
    };


    return (
        <div className="flex flex-col h-full bg-gray-800 text-white w-64 fixed top-0 left-0 shadow-xl z-10">
            
            {/* Encabezado/Logo */}
            <div className="p-4 text-2xl font-extrabold border-b border-gray-700 bg-gray-900 shadow-inner">
                 <span className="text-indigo-400">Medical</span> Cleth
            </div>
            
            {/* Enlaces de Navegación */}
            <nav className="flex-grow p-4 overflow-y-auto">
                <ul className="space-y-1">
                    {navLinks.map((link) => (
                        <li key={link.to}>
                            <NavLink
                                to={link.to}
                                className={({ isActive }) => 
                                    `flex items-center p-3 rounded-xl transition duration-150 font-medium text-sm
                                    ${
                                        isActive 
                                            ? 'bg-indigo-600 text-white shadow-md' 
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                    }`
                                }
                            >
                                {/* USANDO EL NUEVO COMPONENTE DE ÍCONOS MODERNOS */}
                                <IconComponent name={link.icon} />
                                {link.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>

            {/* Enlace de Cerrar Sesión */}
            <div className="p-4 border-t border-gray-700">
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center p-3 text-red-300 bg-gray-700 rounded-xl font-medium hover:bg-red-600 hover:text-white transition duration-200 shadow-inner"
                >
                    {/* Usando el ícono de LogOut de Lucide React */}
                    <LogOut className="mr-3 w-5 h-5" /> 
                    Cerrar sesión
                </button>
            </div>
        </div>
    );
}